import { SelectOption } from 'global';
import {
  ContactAssignmentForCreditSubmissionDetails,
  CreditSubmissionDetailsDialogFormValues,
  DeclineReasonOption,
  SubmissionDeclineReasonCategory,
} from '../types';
import { translateStageCodes } from 'utils';
import { CreditSubmissionSelectOptionsResponse } from '../api/types';
import moment from 'moment/moment';
import {
  CreditSubmissionAssignmentTitle,
  CreditTiersResult,
  UpsertCreditSubmissionAssignmentTitlesInput,
} from '@trnsact/trnsact-shared-types';
import { ScoreTier } from 'modules/financeProgram';
import { keyBy } from 'utils/arrayUtils';
import { LenderAssignmentTitles, UserProfile } from 'types';

export const getSubmissionDeclineReasonsOptions = (creditSubmissionDeclineReasons: any[]): DeclineReasonOption[] => {
  return creditSubmissionDeclineReasons
    .filter(({ isActive }) => isActive)
    .map(csdr => ({ label: csdr.label, category: csdr.category, value: csdr.creditSubmissionDeclineReasonId }));
};

export const getAddEditDeclineReasonCategoryOptions = (): SelectOption[] => {
  return Object.keys(SubmissionDeclineReasonCategory).map(key => {
    return {
      label: SubmissionDeclineReasonCategory[key as keyof typeof SubmissionDeclineReasonCategory],
      value: SubmissionDeclineReasonCategory[key as keyof typeof SubmissionDeclineReasonCategory],
    };
  });
};

const sortCSByCreateDateTime = (cs: CreditSubmissionSelectOptionsResponse['creditSubmissionsSelectOptions'] = []) =>
  cs.toSorted((a, b) => moment(b.createdDateTime).valueOf() - moment(a.createdDateTime).valueOf());

export const getFundedSubmissionOptions = (
  creditSubmissionsOptions: CreditSubmissionSelectOptionsResponse['creditSubmissionsSelectOptions'] = []
): Array<SelectOption & { dynamicCSId: string; decision: string }> => {
  const trustyCS = creditSubmissionsOptions.filter(Boolean);
  const sortedCS = sortCSByCreateDateTime(trustyCS);

  return sortedCS.map(cs => ({
    value: cs.value,
    dynamicCSId: cs.dynamicCSId,
    decision: cs.decision,
    label: `${cs.lenderName}: ${translateStageCodes(cs.creditSubStage)}, ${cs.decision}`,
  }));
};

export const getFinanceProgramTierValues = (tiers: ScoreTier[], scoreCardResult: CreditTiersResult) => {
  const tierConfig = tiers.find(tier => tier.name === scoreCardResult.highestTierPassed);
  return tierConfig
    ? {
        advancePayment: tierConfig.advancePayment ? tierConfig.advancePayment.toString() : undefined,
        commonRate: tierConfig.interestRate ?? undefined,
        maxApprovalAmount: tierConfig.maxApprovalAmount ?? undefined,
        minimumRequiredDownPayment: tierConfig.suggestedDownPaymentPercentage ?? undefined,
        terms: tierConfig.terms ?? [],
      }
    : {};
};

export const getContactsAssignmentsForDetailsCard = (
  savedAssignmentTitles: CreditSubmissionAssignmentTitle[],
  creditSubmissionId: string
): ContactAssignmentForCreditSubmissionDetails[] => {
  const savedAssignmentsByTitle = keyBy(savedAssignmentTitles, 'title') as Record<
    LenderAssignmentTitles,
    CreditSubmissionAssignmentTitle
  >;
  return Object.values(LenderAssignmentTitles).map(title => {
    const typedTitle = title as LenderAssignmentTitles;
    const savedAssignment = savedAssignmentsByTitle[typedTitle];
    return {
      title: typedTitle,
      creditSubmissionId,
      userProfileId: savedAssignment?.userProfileId ?? null,
      creditSubmissionAssignmentTitleId: savedAssignment?.creditSubmissionAssignmentTitleId ?? null,
    };
  });
};

export const getDetailsDialogDefaultFormValues = (
  contactsAssignments: ContactAssignmentForCreditSubmissionDetails[],
  contactsById: Record<string, UserProfile>
): CreditSubmissionDetailsDialogFormValues => {
  return {
    contactsAssignments: contactsAssignments.map(ca => {
      return {
        title: ca.title,
        contact: ca.userProfileId ? { value: ca.userProfileId, label: contactsById[ca.userProfileId].fullName! } : null,
      };
    }),
  };
};

export const getUpsertCreditSubmissionAssignmentTitlesInput = (
  contactsAssignments: ContactAssignmentForCreditSubmissionDetails[],
  formData: CreditSubmissionDetailsDialogFormValues
): UpsertCreditSubmissionAssignmentTitlesInput => {
  return {
    creditSubmissionAssignmentTitles: contactsAssignments.map(ca => {
      const formvalue = formData.contactsAssignments.find(fca => fca.title === ca.title);
      return {
        ...ca,
        userProfileId: formvalue?.contact?.value ?? null,
      };
    }),
  };
};
